import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@/components/ui';
import {
  getOrganizationCaseAccessLog,
  type OrganizationAccessRow,
} from '@/api/services/hrCases.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { accessActionText, formatCaseDate } from './caseFormat';

const PAGE_SIZE = 50;

function timeOf(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** Rows arrive newest first; consecutive rows of one day share a heading. */
function byDay(rows: OrganizationAccessRow[]): Array<{ day: string; rows: OrganizationAccessRow[] }> {
  const days: Array<{ day: string; rows: OrganizationAccessRow[] }> = [];
  for (const row of rows) {
    const day = formatCaseDate(row.at);
    const last = days[days.length - 1];
    if (last?.day === day) last.rows.push(row);
    else days.push({ day, rows: [row] });
  }
  return days;
}

/**
 * Every case read, download, export, and command across the organization, for
 * owners and admins. The server leaves out any case the reader is a party to.
 */
export function OrganizationAccessLogDialog({
  onClose,
}: {
  onClose: () => void;
}): React.ReactElement {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['hr-cases-access-log', page],
    queryFn: () => getOrganizationCaseAccessLog({ page, limit: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    staleTime: 0,
  });
  const rows = Array.isArray(data?.data) ? data.data : [];
  const hasPages = page > 1 || Boolean(data?.hasMore);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="flex w-[calc(100vw-1rem)] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle>Case access log</DialogTitle>
          <DialogDescription>
            Who opened, downloaded, exported, or changed each case, across the organization. Cases
            you are involved in are left out.
          </DialogDescription>
        </DialogHeader>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
          aria-live="polite"
          aria-busy={isFetching}
        >
          {isLoading ? (
            <div className="flex justify-center py-10" role="status">
              <Spinner />
              <span className="sr-only">Loading the access log</span>
            </div>
          ) : error ? (
            <p className="text-sm text-danger" role="alert">
              {getErrorMessage(error)}
            </p>
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-600 dark:text-gray-300">
              No case access recorded yet.
            </p>
          ) : (
            <div className="space-y-5">
              {byDay(rows).map(({ day, rows: dayRows }) => (
                <section key={day} aria-label={day}>
                  <h3 className="mb-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {day}
                  </h3>
                  <ol className="divide-y divide-gray-100 rounded-xl border border-gray-200 text-sm dark:divide-gray-700/70 dark:border-gray-700">
                    {dayRows.map((row) => (
                      <li
                        key={row._id}
                        className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-baseline gap-x-3 gap-y-0.5 px-4 py-2.5 sm:grid-cols-[4.5rem_minmax(0,1fr)_auto]"
                      >
                        <time
                          dateTime={row.at}
                          className="text-xs tabular-nums text-gray-500 dark:text-gray-400"
                        >
                          {timeOf(row.at)}
                        </time>
                        <p className="min-w-0 text-gray-700 dark:text-gray-200">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            {row.actor?.name ?? 'A former user'}
                          </span>{' '}
                          {accessActionText(row)}
                        </p>
                        <span className="col-start-2 font-mono text-xs text-gray-500 sm:col-start-auto sm:text-right dark:text-gray-400">
                          {row.case?.reference ?? '—'}
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </div>

        {hasPages ? (
          <DialogFooter className="flex-row items-center justify-between sm:justify-between">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Page {page} · newest first
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 1 || isFetching}
                onClick={() => setPage((value) => Math.max(1, value - 1))}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                Newer
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!data?.hasMore || isFetching}
                onClick={() => setPage((value) => value + 1)}
              >
                Older
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
